import { getAssignmentUploadsStatus } from "@/queries/formality-request/intern-forms.api";
import { cancelUpload, type InProgressUpload } from "@/queries/uploads";
import type { PendingUpload } from "@sdt/render-form-engine";

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * El guard responde 422 sin `errors`; un 422 con `errors` es de validación de
 * campos y no hace falta consultar las subidas.
 */
const isGuardCandidate = (error: unknown): boolean => {
  if (!isObjectRecord(error) || !isObjectRecord(error.response)) return false;
  const { status, data } = error.response;
  if (status !== 422) return false;
  return !(isObjectRecord(data) && isObjectRecord(data.errors));
};

const toPendingUpload = (upload: InProgressUpload): PendingUpload => ({
  id: upload.id,
  filename: upload.original_filename,
  fieldName: upload.form_field_key,
  sizeBytes: upload.size_bytes,
});

/**
 * Las subidas en curso de la asignación según el backend (PPSS-4671 §5), en el
 * formato del motor, o
 * `null` si no hay ninguna. Un fallo al consultar también da `null`: el envío
 * sigue y, si algo lo bloquea, el guard del backend lo rechaza.
 */
export const findPendingUploads = async (
  assignmentId: string,
): Promise<PendingUpload[] | null> => {
  if (!assignmentId) return null;
  try {
    const status = await getAssignmentUploadsStatus(assignmentId, {
      metadata: { skip401Redirect: true },
    });
    if (!status.has_pending_uploads || status.in_progress.length === 0) {
      return null;
    }
    return status.in_progress.map(toPendingUpload);
  } catch {
    return null;
  }
};

/**
 * Respaldo de la consulta previa: si el envío falló por el guard (una subida
 * empezó entre la consulta y el envío), devuelve las subidas que lo bloquean.
 * No compara el texto del mensaje: la fuente de verdad es
 * `GET …/assignments/{a}/uploads`. Devuelve `null` en cualquier otro caso para
 * que el llamador muestre el error de siempre.
 */
export const findSubmitBlockingUploads = async (
  error: unknown,
  assignmentId: string,
): Promise<PendingUpload[] | null> =>
  isGuardCandidate(error) ? findPendingUploads(assignmentId) : null;

/**
 * Cancela las subidas indicadas. Un `DELETE` que falla no detiene a los demás:
 * una subida que acabó mientras tanto da 422 y ya no bloquea. Lo que quede
 * pendiente lo encuentra la consulta que el motor repite antes de enviar.
 */
export const releaseBlockingUploads = async (
  uploadIds: string[],
): Promise<void> => {
  await Promise.allSettled(
    uploadIds.map((id) =>
      cancelUpload(id, { metadata: { skip401Redirect: true } }),
    ),
  );
};
